import Button from "@mui/material/Button";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import AppBar from "./AppBar";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import {
  ACTIONS,
  createImageDataByImageAndResource,
  getFirstRenderPartByResource,
  getImageResourceSize,
  getPartRefById,
  getResource,
  getTemplateImageRotation,
  getUserImageResources,
} from "../../../../../Providers/TemplateInstanceProvider";
import { Trans, useTranslation } from "react-i18next";
import { Tab, Tabs } from "@mui/material";
import ImageInbox from "./ImageInbox";
import Icon from "@mui/material/Icon";
import { calculateCropRect } from "../../../../../utils";
import TemplateContainer from "./TemplateContainer";
import EpmsImage from "../../../../../components/Common/EpmsImage";
import ToolbarContainer from "./ToolbarContainer";
import ToolbarContent from "./ToolbarContent";
import ToolbarToast from "./ToolbarToast";
import ActionsContainer from "./ActionsContainer";
import ExitDialog from "./ExitDialog";
import { useGreenScreenBackgrounds } from "../../../Providers/GreenScreenBackgroundsProvider";
import GreenScreenBackgrounds from "./GreenScreenBackgrounds";
import { usePortal } from "../../../../../Providers/PortalProvider";
import { callAPI } from "../../../../../lib/common";
import { usePictureSets } from "../../../../hooks/usePictureSets";
import ChangeAllImagesWarningDialog from "./ChangeAllImagesWarningDialog";
import ChangeAllPackagePicturesWarningDialog from "./ChangeAllPackagePicturesWarningDialog";
import ImageAllowedValidator from "../../../../../components/Template/ImageAllowedValidator";
import {
  getCropRectForImageWithRotation,
  imageSupportsBackground,
} from "@/react/components/Template/common";
import Popup, {
  PopupActions,
  PopupButton,
  PopupContent,
  PopupHeading,
  PopupTextContent,
} from "@/react/components/Common/Popup";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    backgroundColor: "white",
    flexGrow: 0,
    flexShrink: 0,
  },
  button: {
    backgroundColor: "black",
    color: "white",
    width: "64px",
    height: "64px",
    justifyContent: "center",
    display: "center",
    borderRadius: "32px",
    "&:hover": {
      color: "white",
      backgroundColor: "black",
    },
    "&.Mui-disabled": {
      color: "white",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  tabs: {
    "&.MuiTabs-root": {
      minHeight: 0,
    },
    "& .MuiTab-root": {
      fontSize: "12px",
      minHeight: "40px",
      fontWeight: 400,
      paddingBottom: 0,
    },
  },
  buttonWithText: {
    fontSize: "16px",
  },

  toolBarContainer: {
    width: "100%",
    overflowX: "auto",
  },
  addImagesButton: {
    height: "64px",
    width: "64px",
    backgroundColor: "#ccc !important",
  },
  toolbarItems: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    marginBottom: theme.spacing(2),
    display: "flex",
    flexWrap: "nowrap",
    "& > li > button": {
      width: "64px",
      height: "64px",
      marginLeft: theme.spacing(1),
      backgroundColor: "transparent",
      border: "none",
      padding: 0,
      borderRadius: "2px",
      overflow: "hidden",

      "& > img, & > canvas": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
    },
  },
  rightMenuSpacer: {
    width: theme.spacing(1),
  },
  editImageToolsContainer: {
    padding: theme.spacing(2),
  },
  editImageToolList: {
    display: "flex",
    justifyContent: "space-around",
    listStyle: "none",
    margin: 0,
    padding: 0,
    "& > li": {
      display: "inline-block",
    },
    "& button": {
      fontSize: "12px",
      display: "flex",
      flexDirection: "column",
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      width: "76px",
      height: "74px",
      border: "none",
      borderBottom: "2px solid " + theme.palette.primary.main,
      "&.selected": {
        borderBottom: "2px solid " + theme.palette.secondary.main,
      },
      "& > div": {
        width: "100%",
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  },
  imageTool: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    "& > div:first-child": {
      marginBottom: theme.spacing(1),
    },
  },
  tab: {
    color: theme.palette.primary.main,
  },
  disabledTab: {
    "&.MuiTab-root": {
      opacity: 0.5,
    },
  },
  dummyImage: {
    display: "none",
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{ flexGrow: 1 }}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const ImagePage = ({
  changeMenu,
  allowSubjectChange,
  handleChangeAllPackagePictures,
}) => {
  const classes = useStyles();
  const [state, dispatch] = useTemplateInstance();
  const [currentTab, setCurrentTab] = useState(0);
  const { t } = useTranslation();
  const [localResource, setLocalResource] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);
  const [showPinchToast, setShowPinchToast] = useState(false);
  const [backgrounds, loading] = useGreenScreenBackgrounds();
  const { currentPictureSet } = usePictureSets();
  const originalInstance = useRef(state);
  const [changeAllImagesWarning, setChangeAllImagesWarning] = useState(false);
  const portal = usePortal();

  const originalImage = useRef();
  const [
    showChangeAllPackagePicturesWarning,
    setShowChangeAllPackagePicturesWarning,
  ] = useState(false);
  const [showBackgroundNotSupported, setShowBackgroundNotSupported] =
    useState(false);

  // Greenscreen backgrounds only apply to greenscreen images (PNGs). When the
  // selected picture is a non-greenscreen image (e.g. a JPG group photo), the
  // "Backgrounds" tab is greyed and tapping it explains why instead of opening.
  const backgroundSupported = imageSupportsBackground(
    localResource?.image,
    currentPictureSet,
  );
  const backgroundsTabIndex = portal.options.disableImageToolbox ? 1 : 2;

  useEffect(() => {
    // If the user swaps to a non-greenscreen image while on the Backgrounds
    // tab, move them back to the image inbox.
    if (currentTab === backgroundsTabIndex && !backgroundSupported) {
      setCurrentTab(0);
    }
  }, [backgroundSupported, currentTab, backgroundsTabIndex]);

  useEffect(() => {
    // I guess this is to initialize after the first render?
    setTimeout(() => {
      const partRef = getPartRefById(state.currentPartRefId, state.template);
      console.log(
        "The part ref",
        partRef,
        state.template,
        state.currentResourceId,
      );
      const resource = getResource(partRef, state.currentResourceId, state);
      originalImage.current = resource.image;
      setLocalResource(resource);
    }, 0);
  }, []);

  useEffect(() => {
    if (!localResource?.image?.uuid) {
      return;
    }

    if (localResource.disableZoom || portal.options.disableImageToolbox) {
      return;
    }

    // FIXME: Don't support rotated images for now:
    const renderPart = getFirstRenderPartByResource(
      localResource,
      state.template,
    );
    if (renderPart.rotation) {
      return;
    }

    if (!sessionStorage.getItem("pinch-zoom-info-shown")) {
      setShowPinchToast(true);
      sessionStorage.setItem("pinch-zoom-info-shown", 1);
      const timer = setTimeout(() => {
        setShowPinchToast(false);
      }, 10000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [localResource && localResource.image.uuid]);

  const handleClosePinchToast = () => {
    setShowPinchToast(false);
  };

  const handleImageLoad = useCallback(() => {
    console.log("Dummy Image loaded!!!");
    setLocalResource((resource) => {
      return {
        ...resource,
        image: {
          ...resource.image,
          loading: false,
        },
      };
    });
  }, []);

  const handleSave = (skipImageNumCheck) => {
    if (!localResource.image.uuid) {
      changeMenu("main");
      return;
    }

    const { picturePosition, ...rest } = localResource.image;
    const image = localResource.image;
    if (image.picturePosition) {
      const renderPart = getFirstRenderPartByResource(
        localResource,
        state.template,
      );
      const renderPartSize = {
        width: renderPart.size.width * state.scaleRatio,
        height: renderPart.size.height * state.scaleRatio,
      };

      console.log("Render part size", renderPartSize);

      const imageSize = getImageResourceSize(image, renderPart);
      const cropRect = calculateCropRect(
        localResource.image.picturePosition,
        imageSize,
        renderPartSize,
      );
      rest.cropRect = {
        x: cropRect.x,
        y: cropRect.y,
        width: cropRect.width,
        height: cropRect.height,
      };
    }

    if (
      (originalImage.current.uuid != localResource.image.uuid ||
        originalImage.current.background?.uuid !=
          localResource.image.background?.uuid ||
        originalImage.current.overrideEffect !=
          localResource.image.overrideEffect) &&
      !skipImageNumCheck
    ) {
      // Do package change warning only if we change image:
      if (
        allowSubjectChange == 4 &&
        originalImage.current.uuid != localResource.image.uuid
      ) {
        setShowChangeAllPackagePicturesWarning(true);
        return;
      } else if (
        getUserImageResources(state).length > 4 &&
        state.template.type != "CALENDAR" &&
        state.template.type != "CARDS"
      ) {
        setChangeAllImagesWarning(rest);
        return;
      }
    }

    console.log("The rest", rest);

    dispatch({
      type: ACTIONS.UPDATE_CURRENT_IMAGE,
      image: rest,
    });

    setLocalResource(null);
    setTimeout(() => {
      changeMenu("main");
    }, 300);
  };

  const handleUpdateRotation = (rotation) => {
    setDirty(true);

    // Don't include picturePosition, as it's reset by crop:
    setLocalResource((resource) => {
      const renderPart = getFirstRenderPartByResource(
        localResource,
        state.template,
      );

      const cropRect = getCropRectForImageWithRotation(
        {
          width: localResource.image.width,
          height: localResource.image.height,
        },
        renderPart,
        rotation,
        portal.webshop.template.portraitOrientationGravity,
      );

      console.log("Created crop rect", cropRect);

      const image = {
        ...localResource.image,
        uuid: localResource.image.uuid,
        width: localResource.image.width,
        height: localResource.image.height,
        rotation,
        cropRect,
      };

      const newResource = {
        ...resource,
        image: {
          ...image,
          loading: true,
          overrideEffect: resource.image.overrideEffect || "original",
          background: resource.image.background,
          backgroundRotationFollowImage:
            resource.image.backgroundRotationFollowImage,
        },
      };
      console.log("The new resource", newResource);
      return newResource;
    });
  };

  const updateEffect = (effect) => {
    let renderPart = getFirstRenderPartByResource(
      localResource,
      state.template,
    );
    const currentEffect =
      localResource.image.overrideEffect ||
      renderPart.image.effect ||
      "original";
    if (currentEffect == effect) {
      console.log("Skipping effect as its the same as current", effect);
      return;
    }
    setDirty(true);
    setLocalResource((resource) => {
      console.log("Handle effect update", effect);
      const newResource = {
        ...resource,
        image: {
          ...resource.image,
          overrideEffect: effect,
          loading: true,
        },
      };
      return newResource;
    });
  };

  const updateBackground = (background) => {
    setDirty(true);
    setLocalResource((resource) => {
      const newResource = {
        ...resource,
        image: {
          ...resource.image,
          background,
          loading: false,
        },
      };
      return newResource;
    });
  };

  const handleCropComplete = (pos) => {
    setDirty(true);
    setLocalResource((resource) => {
      const { picturePosition, ...rest } = resource.image;

      const renderPart = getFirstRenderPartByResource(
        resource,
        state.template,
      );
      const renderPartSize = {
        width: renderPart.size.width * state.scaleRatio,
        height: renderPart.size.height * state.scaleRatio,
      };

      const imageSize = getImageResourceSize(resource.image, renderPart);
      const cropRect = calculateCropRect(pos, imageSize, renderPartSize);

      const newResource = {
        ...resource,
        image: {
          ...rest,
          cropRect: {
            x: cropRect.x,
            y: cropRect.y,
            width: cropRect.width,
            height: cropRect.height,
          },
        },
      };
      console.log("New resource", resource, rest, newResource);
      return newResource;
    });
  };

  const handleCropUpdate = (pos) => {
    console.log("Crop update");
    setDirty(true);

    // Remove dialog if moving:
    setShowPinchToast(false);

    setLocalResource((resource) => {
      console.log("Handle crop update", pos);
      const newResource = {
        ...resource,
        image: {
          ...resource.image,
          // Picture position is temp while editing, so we don't have to recalc imagepos => cropRect => imagePos
          picturePosition: pos,
        },
      };
      return newResource;
    });
  };

  const setLocalResourceImage = (image) => {
    setLocalResource((resource) => {
      const newResource = createImageDataByImageAndResource(
        image,
        resource,
        state.template,
        portal.webshop.template.portraitOrientationGravity,
      );

      console.log("The new resource jalla", newResource);
      return newResource;
    });
  };

  const _handleChangeAllPackagePictures = () => {
    handleChangeAllPackagePictures(localResource.image);
  };

  const handleChangeAllImages = () => {
    const image = { ...changeAllImagesWarning };
    const sourceRenderPart = getFirstRenderPartByResource(
      localResource,
      state.template,
    );
    // Hack, as getTemplateImageRotation need it:
    image.size = {
      width: image.width,
      height: image.height,
    };

    const userImageResources = {};

    const imageAllowedValidator = new ImageAllowedValidator(
      state.template,
      portal.webshop.template,
    );

    for (const userResource of getUserImageResources(state)) {
      try {
        console.log("Checking if image is allowed", image, userResource);
        imageAllowedValidator.imageAllowedInResource(image, userResource);
      } catch (err) {
        console.log("Unable to replace all pictures", err);
        continue;
      }

      const renderPart = getFirstRenderPartByResource(
        userResource,
        state.template,
      );
      if (!renderPart) {
        continue;
      }

      // Pixel crops also fit proportionally scaled cells. Different placement
      // rotations or adaptations need their own fitting calculation.
      const preserveCrop =
        image.cropRect &&
        sourceRenderPart &&
        sourceRenderPart.rotation === renderPart.rotation &&
        sourceRenderPart.image.adaption === renderPart.image.adaption &&
        sourceRenderPart.size.width * renderPart.size.height ===
          renderPart.size.width * sourceRenderPart.size.height;
      const rotation = preserveCrop
        ? image.rotation
        : getTemplateImageRotation(state.template, userResource, image);
      const cropRect = preserveCrop
        ? { ...image.cropRect }
        : getCropRectForImageWithRotation(
            { width: image.width, height: image.height },
            renderPart,
            rotation,
            portal.webshop.template.portraitOrientationGravity,
          );

      const newResource = {
        ...userResource,
        image: {
          width: image.size.width,
          height: image.size.height,
          webshopId: image.webshopId,
          uuid: image.uuid,
          cropRect,
          rotation,
          overrideEffect: image.overrideEffect || "original",
          background: image.background || null,
          greenscreen: image.greenscreen,
        },
      };

      userImageResources[userResource.fullId] = newResource;
    }

    dispatch({
      type: ACTIONS.SET_RESOURCES,
      resources: userImageResources,
    });

    handleSave(true);
  };

  const handleSelectImage = (image) => {
    setDirty(true);

    // Before we can do anything else, set the resource to loading:
    setLocalResource((resource) => {
      console.log("Selected image (Local resource)", resource, image);
      return {
        ...resource,
        image: {
          ...resource.image,
          loading: true,
          cropRect: null,
        },
      };
    });

    // If it's a group picture, we might need to update name tagging, strings etc:
    if (image.groupPicture) {
      callAPI("/api/webshop/templates/fill-user-strings-by-picture", "POST", {
        subject: {
          uuid: currentPictureSet.subject.uuid,
        },
        template: {
          uuid: state.template.uuid,
        },
        picture: {
          uuid: image.uuid,
        },
        resource: {
          id: localResource.id,
        },
      })
        .then((data) => {
          dispatch({
            type: ACTIONS.SET_RESOURCES,
            resources: data.userStringResources,
          });
        })
        .finally(() => {
          setLocalResourceImage(image);
        });
    } else {
      setLocalResourceImage(image);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();

    if (dirty) {
      setShowSaveChangesDialog(true);
    } else {
      setLocalResource(null);
      setTimeout(() => {
        changeMenu("main");
      }, 300);
    }
  };

  const doClose = () => {
    dispatch({
      type: ACTIONS.SET_TEMPLATE_INSTANCE,
      templateInstance: originalInstance.current,
    });
    changeMenu("main");
  };

  let tabPanelIndex = 0;
  const tabPanels = [
    <TabPanel key="image-inbox" value={currentTab} index={tabPanelIndex}>
      <ImageInbox
        handleSelectImage={handleSelectImage}
        resource={localResource}
        allowSubjectChange={allowSubjectChange}
        template={state.template}
      />
    </TabPanel>,
  ];
  tabPanelIndex++;
  if (!portal.options.disableImageToolbox) {
    tabPanels.push(
      <TabPanel key="image-options" value={currentTab} index={tabPanelIndex}>
        <EditImageOptions
          resource={localResource}
          handleUpdateEffect={updateEffect}
          handleUpdateRotation={handleUpdateRotation}
        />
      </TabPanel>,
    );
    tabPanelIndex++;
  }
  tabPanels.push(
    <TabPanel
      key="green-screen-backgrounds"
      value={currentTab}
      index={tabPanelIndex}
    >
      <GreenScreenBackgrounds
        resource={localResource}
        handleUpdate={updateBackground}
      />
    </TabPanel>,
  );
  tabPanelIndex++;

  const disableChangeCrop =
    localResource?.disableZoom || portal.options.disableImageToolbox;

  console.log("Zoom resource", localResource);

  return (
    <div className={classes.mainContainer}>
      <AppBar onCancel={handleCancel} />

      <TemplateContainer
        zoomResource={localResource}
        dispatch={dispatch}
        handleUserImageClick={
          localResource
            ? null
            : () => {
                // Noop
              }
        }
        handleCropUpdate={!disableChangeCrop && handleCropUpdate}
        handleCropComplete={!disableChangeCrop && handleCropComplete}
      />

      <ToolbarContainer>
        {showPinchToast && (
          <ToolbarToast onClick={handleClosePinchToast}>
            <Trans>
              Move the picture with your finger. Pinch with two fingers to zoom
              in/out.
            </Trans>
          </ToolbarToast>
        )}
        <ToolbarContent>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Tabs
              value={currentTab}
              textColor="secondary"
              variant="fullWidth"
              className={classes.tabs}
              onChange={(e, newVal) => {
                if (newVal === backgroundsTabIndex && !backgroundSupported) {
                  setShowBackgroundNotSupported(true);
                  return;
                }
                setCurrentTab(newVal);
              }}
            >
              <Tab
                label={t("Image inbox")}
                classes={{ textColorSecondary: classes.tab }}
              />
              {!portal.options.disableImageToolbox && (
                <Tab
                  label={t("Edit image")}
                  disabled={!localResource?.image?.uuid}
                  classes={{ textColorSecondary: classes.tab }}
                />
              )}
              {!localResource?.disableBackground &&
                backgrounds &&
                backgrounds.length > 0 && (
                  <Tab
                    label={t("Backgrounds")}
                    // Keep the tab clickable (not MUI-disabled) when the image
                    // is unsupported so we can explain why via a dialog; only
                    // truly disable it when there is no image yet.
                    disabled={!localResource?.image?.uuid}
                    className={
                      backgroundSupported ? undefined : classes.disabledTab
                    }
                    classes={{ textColorSecondary: classes.tab }}
                  />
                )}
            </Tabs>

            {tabPanels}
          </div>
        </ToolbarContent>
        <ActionsContainer>
          <Button color="primary" onClick={handleCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            color="primary"
            onClick={() => handleSave(false)}
            disabled={!localResource?.image.uuid}
          >
            <Trans>Done</Trans>
          </Button>
        </ActionsContainer>
      </ToolbarContainer>
      <DummyImage
        onLoad={handleImageLoad}
        resource={localResource}
        state={state}
      />
      <ExitDialog
        open={showSaveChangesDialog}
        handleCancel={() => setShowSaveChangesDialog(false)}
        handleOk={doClose}
      />
      <ChangeAllImagesWarningDialog
        open={changeAllImagesWarning}
        handleYes={handleChangeAllImages}
        handleNo={() => handleSave(true)}
      />
      <ChangeAllPackagePicturesWarningDialog
        open={showChangeAllPackagePicturesWarning}
        handleConfirm={_handleChangeAllPackagePictures}
        handleCancel={() => setShowChangeAllPackagePicturesWarning(false)}
      />
      {showBackgroundNotSupported && (
        <BackgroundNotSupportedDialog
          onClose={() => setShowBackgroundNotSupported(false)}
        />
      )}
    </div>
  );
};

export default ImagePage;

const BackgroundNotSupportedDialog = ({ onClose }) => {
  return (
    <Popup open={true} onClose={onClose}>
      <PopupHeading>
        <Trans>Backgrounds not available</Trans>
      </PopupHeading>

      <PopupContent>
        <PopupTextContent>
          <Trans>
            You can't use green screen backgrounds with this picture
          </Trans>
        </PopupTextContent>
      </PopupContent>
      <PopupActions>
        <PopupButton onClick={onClose}>
          <Trans>Ok</Trans>
        </PopupButton>
      </PopupActions>
    </Popup>
  );
};

const EditImageOptions = ({
  resource,
  handleUpdateEffect,
  handleUpdateRotation,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  console.log("The resource", resource);

  const handleFilter = (filter) => {
    handleUpdateEffect(filter);
  };

  const handleRotation = () => {
    console.log("Handle rotation", resource);
    let rotation = resource.image.rotation || 0;
    rotation -= 90;

    if (rotation < 0) {
      rotation = 270;
    }

    handleUpdateRotation(rotation);
  };

  return (
    <div className={classes.editImageToolsContainer}>
      <ul className={classes.editImageToolList}>
        <li>
          <ImageTool
            heading={t("Rotate")}
            icon={<Icon>rotate_90_degrees_ccw</Icon>}
            onClick={handleRotation}
          />
        </li>
        <li>
          <ImageTool
            heading={t("Black/White")}
            icon={<Icon>photo_filter</Icon>}
            selected={resource?.image.overrideEffect == "bw"}
            onClick={() => handleFilter("bw")}
          />
        </li>
        <li>
          <ImageTool
            heading={t("Sepia")}
            icon={<Icon>photo_filter</Icon>}
            selected={resource?.image.overrideEffect == "sepia"}
            onClick={() => handleFilter("sepia")}
          />
        </li>
        <li>
          <ImageTool
            heading={t("Original")}
            icon={<Icon>photo_filter</Icon>}
            selected={
              !resource?.image.overrideEffect ||
              resource.image.overrideEffect == "original"
            }
            onClick={() => handleFilter("original")}
          />
        </li>
      </ul>
    </div>
  );
};

// FIXME: This is probably a race condition here? image load effect can come from the wrong image?
const DummyImage = ({ onLoad, resource, state }) => {
  const classes = useStyles();
  console.log("Dummy resource", resource);
  const imageSize = 640;

  const [style, effect, rotation] = useMemo(() => {
    if (!resource?.image.uuid) {
      return [];
    }

    console.log("Stuff. dummy resource");
    const renderPart = getFirstRenderPartByResource(resource, state.template);
    const effect =
      resource.image.overrideEffect || renderPart.image.effect || "original";
    const rotation = resource.image.rotation;

    console.log("Dummy resource", renderPart);

    const style = {
      width: renderPart.size.width * state.scaleRatio + "px",
      height: renderPart.size.height * state.scaleRatio + "px",
    };
    console.log("Dummy image style", style, state);
    return [style, effect, rotation];
  }, [
    state.scaleRatio,
    resource?.image.overrideEffect,
    resource?.image.rotation,
  ]);

  if (!resource?.image?.uuid) {
    return null;
  }

  console.log("Dummy image", resource.image.uuid, rotation, effect);

  return (
    <div className={classes.dummyImage} style={style}>
      <EpmsImage
        onLoad={onLoad}
        uuid={resource.image.uuid}
        rotation={rotation}
        effect={effect}
        size={imageSize}
        forceVisible={true}
      />
    </div>
  );
};

const ImageTool = ({ heading, icon, onClick, selected }) => {
  const classes = useStyles();
  return (
    <button onClick={onClick} className={selected ? "selected" : ""}>
      <div className={classes.imageTool}>
        <div>{icon}</div>
        <div>{heading}</div>
      </div>
    </button>
  );
};
